import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import cx from 'classnames';

import { addNotification } from 'actions/globalActions';

import Button from 'components/common/Button';

const NewGroupForm = ({ onSubmitForm }) => {

  const dispatch = useDispatch();

  const [selectedName, setSelectedName] = useState('');
  const [selectedIsBot, setSelectedIsBot] = useState(false);
  const [selectedIsWhitelist, setSelectedIsWhitelist] = useState(false);
  const [selectedIsBlacklist, setSelectedIsBlacklist] = useState(false);
  const [selectedIPList, setSelectedIPList] = useState([]);

  const handleNameChange = (e) => {
    const { value } = e.target;
    setSelectedName(value);
  };

  const handleIsBotChange = () => {
    setSelectedIsBot(!selectedIsBot);
  };

  const handleIsWhitelistChange = () => {
    setSelectedIsWhitelist(!selectedIsWhitelist);
  };

  const handleIsBlacklistChange = () => {
    setSelectedIsBlacklist(!selectedIsBlacklist);
  };

  const handleIPListChange = (e) => {
    const { value } = e.target;
    const ipArray = value.split('\n');
    setSelectedIPList(ipArray);
  };

  const validateIPList = (ipList) => {
    if (ipList.length === 0) {
      return null;
    }
    // IPv4 and IPv6
    const IPRegEx = /((^\s*((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))\s*$)|(^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$))/;
    const badIPs = [];
    ipList.forEach((ip, index) => {
      if (!IPRegEx.test(ip)) {
        badIPs.push(index + 1);
      }
    });
    if (badIPs.length !== 0) {
      return {
        type: 'warning',
        title: 'Invalid IP list',
        description: `${badIPs.length} bad IPs, starting from line ${badIPs[0]}`
      };
    }
    return null;
  };

  const validateName = (name) => {
    // only letters, numbers and whitespaces
    const nameRegEx = /^[A-Za-z0-9 ]+$/;
    if (!nameRegEx.test(name)) {
      return {
        type: 'warning',
        title: 'Invalid group name',
        description: 'Use only letters, numbers and spaces'
      };
    }
    return null;
  };

  const submitForm = () => {
    const validationErrors = [
      validateName(selectedName),
      validateIPList(selectedIPList),
    ].filter(e => e !== null);
    if (validationErrors.length === 0) {
      onSubmitForm({
        name: selectedName,
        isBot: selectedIsBot,
        isWhitelist: selectedIsWhitelist,
        isBlacklist: selectedIsBlacklist,
        ipList: selectedIPList,
      });
      // clear input
      setSelectedName('');
      setSelectedIsBot(false);
      setSelectedIsWhitelist(false);
      setSelectedIsBlacklist(false);
      setSelectedIPList([]);
    } else {
      dispatch(addNotification(validationErrors[0]));
    }
  };

  return (
    <div className="control">
      <div className="field">
        <label className="label">Name</label>
        <input
          className="input"
          name="name"
          type="text"
          value={selectedName}
          onChange={handleNameChange}
        />
        <p className="help">Only letters, numbers and whitespaces allowed</p>
      </div>

      <div className="field">
        <label className="label">Flags</label>
        <div className="control is-flex is-justify-content-space-around">
          <span
            className={cx('icon is-large flag-button', {
              'has-text-primary': selectedIsBot,
              'has-text-grey': !selectedIsBot
            })}
            onClick={handleIsBotChange}
          >
            <i className="fas fa-2x fa-robot"></i>
          </span>
          <span
            className={cx('icon is-large flag-button', {
              'has-text-primary': selectedIsWhitelist,
              'has-text-grey': !selectedIsWhitelist
            })}
            onClick={handleIsWhitelistChange}
          >
            <i className="far fa-2x fa-check-circle"></i>
          </span>
          <span
            className={cx('icon is-large flag-button', {
              'has-text-primary': selectedIsBlacklist,
              'has-text-grey': !selectedIsBlacklist
            })}
            onClick={handleIsBlacklistChange}
          >
            <i className="fas fa-2x fa-ban"></i>
          </span>
        </div>
        <p className="help">Tick the flags that will dictate this group&apos;s behaviour</p>
      </div>

      <div className="field">
        <label className="label">Identified IPs</label>
        <textarea
          className="textarea"
          placeholder="Write your IPs here separated by newline"
          onChange={handleIPListChange}
        >
        </textarea>
        <p className="help">Write your IPs separated by newline</p>
      </div>

      <Button className="is-pulled-right" type="text" onClick={submitForm}>
        Save Changes
      </Button>
    </div>
  );

};

NewGroupForm.propTypes = {
  onSubmitForm: PropTypes.func
};

export default NewGroupForm;
